import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GraduationCap, CheckCircle, ArrowLeft } from 'lucide-react';
import CoursesPageHeader from './CoursesPageHeader';
import Footer from './Footer';
import './Courses.css';

const PURPOSE_OPTIONS = [
  { value: 'social_content', label: 'Контент для соцсетей и блога' },
  { value: 'images', label: 'Работа с изображениями (фото, дизайн)' },
  { value: 'video', label: 'Видео и анимация' },
  { value: 'business_automation', label: 'Автоматизация задач в работе' },
  { value: 'monetization', label: 'Заработок на нейросетях' },
  { value: 'hobby', label: 'Для себя / хобби' },
  { value: 'other', label: 'Другое' },
];

const LEVEL_OPTIONS = [
  { value: 'beginner', label: 'Никогда не пользовался нейросетями' },
  { value: 'tried_tools', label: 'Пробовал ChatGPT и генераторы' },
  { value: 'pikly_user', label: 'Уже работаю в Pikly' },
  { value: 'professional', label: 'Профессионал (дизайн, SMM, видео)' },
];

const FORMAT_OPTIONS = [
  { value: '', label: 'Не выбрано' },
  { value: 'group_online', label: 'Онлайн в группе' },
  { value: 'individual', label: 'Индивидуально' },
  { value: 'corporate', label: 'Корпоративное для команды' },
  { value: 'unknown', label: 'Пока не знаю' },
];

const INITIAL_FORM = {
  name: '',
  phone: '',
  email: '',
  purpose: '',
  purposeOther: '',
  level: '',
  format: '',
  telegram: '',
  comment: '',
  consent: false,
};

function CourseForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.consent) {
      toast.error('Необходимо согласие на обработку персональных данных');
      return;
    }

    if (form.purpose === 'other' && !form.purposeOther.trim()) {
      toast.error('Укажите цель обучения');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/courses/request', {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        purpose: form.purpose,
        purpose_other: form.purpose === 'other' ? form.purposeOther.trim() : null,
        level: form.level,
        preferred_format: form.format || null,
        telegram: form.telegram.trim() || null,
        comment: form.comment.trim() || null,
      });
      setSuccess(true);
      setForm(INITIAL_FORM);
      toast.success('Заявка успешно отправлена!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || 'Проверьте правильность заполнения формы');
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Не удалось отправить заявку. Попробуйте позже.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="courses-page">
      <CoursesPageHeader />

      <div className="courses-container">
        <Link to="/courses" className="courses-back-link">
          <ArrowLeft size={18} />
          Все курсы
        </Link>

        <section className="courses-hero">
          <div className="courses-hero-badge">
            <GraduationCap size={18} />
            Заявка на обучение
          </div>
          <h1>Оставить заявку</h1>
          <p className="courses-hero-subtitle">
            Заполните форму — мы подберём программу под ваши задачи и свяжемся с вами.
          </p>
        </section>

        <section className="courses-form-section">
          <div className="courses-form-card">
            {success ? (
              <div className="courses-success">
                <CheckCircle size={48} color="#6fbf4a" />
                <h2>Заявка принята!</h2>
                <p>Спасибо за интерес! Мы свяжемся с вами, как только откроем курс, и расскажем о программе и формате обучения.</p>
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => setSuccess(false)}
                >
                  Отправить ещё одну заявку
                </button>
              </div>
            ) : (
              <form className="courses-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Имя *</label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Как к вам обращаться"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Телефон *</label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      placeholder="+7 (999) 123-45-67"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="purpose">Зачем нужно обучение *</label>
                  <select
                    id="purpose"
                    name="purpose"
                    value={form.purpose}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Выберите цель</option>
                    {PURPOSE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {form.purpose === 'other' && (
                  <div className="form-group">
                    <label htmlFor="purposeOther">Уточните цель *</label>
                    <input
                      id="purposeOther"
                      type="text"
                      name="purposeOther"
                      value={form.purposeOther}
                      onChange={handleChange}
                      placeholder="Опишите, чему хотите научиться"
                    />
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="level">Ваш уровень *</label>
                    <select
                      id="level"
                      name="level"
                      value={form.level}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Выберите уровень</option>
                      {LEVEL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="format">Предпочтительный формат</label>
                    <select
                      id="format"
                      name="format"
                      value={form.format}
                      onChange={handleChange}
                    >
                      {FORMAT_OPTIONS.map((opt) => (
                        <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="telegram">Telegram</label>
                  <input
                    id="telegram"
                    type="text"
                    name="telegram"
                    value={form.telegram}
                    onChange={handleChange}
                    placeholder="@username"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="comment">Расскажите подробнее</label>
                  <textarea
                    id="comment"
                    name="comment"
                    value={form.comment}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Например: хочу научиться делать Reels с AI, есть опыт в Canva"
                  />
                </div>

                <label className="courses-consent">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={form.consent}
                    onChange={handleChange}
                  />
                  <span>
                    Я согласен на обработку персональных данных в соответствии с{' '}
                    <a href="/privacy_policy.pdf" target="_blank" rel="noopener noreferrer">
                      политикой конфиденциальности
                    </a>
                  </span>
                </label>

                <button type="submit" className="courses-submit-btn" disabled={submitting}>
                  {submitting ? 'Отправляем...' : 'Отправить заявку'}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default CourseForm;
